export const EVENT = {
  END: "transitionend",
  CANCEL: "transitioncancel",
};

const NOOP = () => {};

/**
 * js contolled css transition animation
 * @param {Element} el element to animate
 * @param {object} props css props to animate
 *   @prop {array} props.cssPropName css prop to animate
 *     @prop {string|function} props.cssPropName[0] css prop value from
 *     @prop {string|function} props.cssPropName[1] css prop value to
 * @param {object} options
 *   @prop {number} options.duration transition duration [ms] default: 200
 *   @prop {string} options.easing transition easing default: ease
 *   @prop {function} options.onEnd on transition end callback
 *   @prop {function} options.onCancel on transition cancel callback (triggered on cleanup too)
 * @returns {function} cleanup function
 */
export default function transition(el, props = {}, options = {}) {
  const {
    duration = 200,
    easing = "ease",
    onEnd = NOOP,
    onCancel = NOOP,
    onStart = NOOP,
  } = options;

  const propKeys = Object.keys(props);
  const propsCount = propKeys.length;

  function eachProp(callback) {
    for (let i = 0; i < propsCount; i++) {
      const propKey = propKeys[i];
      callback(propKey, props[propKey]);
    }
  }

  let rafRef;

  /* eslint-disable no-param-reassign */
  el.style.transition = "none";

  rafRef = requestAnimationFrame(() => {
    // set transition from values
    eachProp((propKey, [from = ""]) => {
      el.style[propKey] = typeof from === "function" ? from(el) : from;
    });

    rafRef = requestAnimationFrame(() => {
      onStart();

      // set transition options
      el.style.transition = propKeys
        .map((propKey) => `${propKey} ${duration}ms ${easing}`)
        .join(",");

      rafRef = requestAnimationFrame(() => {
        // set transition to values
        eachProp((propKey, [, to = ""]) => {
          el.style[propKey] = typeof to === "function" ? to(el) : to;
        });
      });
    });
  });

  function onBreakEvent(e) {
    cancelAnimationFrame(rafRef);
    rafRef = null;

    el.removeEventListener(EVENT.END, onBreakEvent);
    el.removeEventListener(EVENT.CANCEL, onBreakEvent);

    el.style.transition = "";

    eachProp((propKey) => {
      el.style[propKey] = "";
    });

    switch (e?.type) {
      case EVENT.END:
        onEnd(el, props);
        break;

      case EVENT.CANCEL:
        onCancel(el, props);
        break;

      default:
        onCancel(el, props);
        break;
    }
  }

  el.addEventListener(EVENT.END, onBreakEvent);
  el.addEventListener(EVENT.CANCEL, onBreakEvent);
  /* eslint-enable no-param-reassign */

  function cleanup() {
    onBreakEvent();
  }

  return cleanup;
}
